import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  from,
  map,
  Observable,
  of,
  retry,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { Template } from '../../interfaces/template.interface';
import { ERROR_PAGE_URL } from '../../pages/not-found-error/not-found-error.page';
import { TemplateService } from '../../services/template.service';
import { handleHttpError } from '../../utils/api';
import { TemplateActions } from '../actions/template.actions';
import { selectSearchedParams } from '../selectors/template.selector';
import { selectActiveOrganisationId } from '../selectors/user.selector';

@Injectable()
export class TemplateEffects {
  public get$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.get),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([{ id }, organisation]) =>
        from(this.service.get(organisation, id)).pipe(
          map((template) => TemplateActions.getSuccess({ template })),
          catchError((error) => of(TemplateActions.getFailure({ error }))),
        ),
      ),
    ),
  );

  public getFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TemplateActions.getFailure),
        tap(async () => await this.router.navigateByUrl(`/${ERROR_PAGE_URL}`)),
      ),
    { dispatch: false },
  );

  public search$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.search),
      withLatestFrom(
        this.store.select(selectActiveOrganisationId),
      ),
      debounceTime(700),
      switchMap(([{ params }, organisation]) => {
        return from(this.service.search(organisation, { ...params })).pipe(
          map(({ items }) => TemplateActions.searchSuccess({ templates: items })),
          catchError((error) => of(TemplateActions.searchFailure({ error }))),
        );
      }),
    ),
  );

  public searchFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.searchFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.create),
      withLatestFrom(
        this.store.select(selectActiveOrganisationId),
        this.store.select(selectSearchedParams),
      ),
      switchMap(([action, organisationId, searchParams]) =>
        from(this.service.create(organisationId, action.payload)).pipe(
          retry(2),
          map((payload: Template) => {
            this.store.dispatch(TemplateActions.search({ params: searchParams }));

            return TemplateActions.createSuccess({ payload });
          }),
          catchError((error) =>
            of(
              TemplateActions.createFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public createFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.createFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.update),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.update(organisationId, action.templateId, action.payload),
        ).pipe(
          map((payload) => TemplateActions.updateSuccess({ payload })),
          catchError((error) =>
            of(
              TemplateActions.updateFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.updateFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public delete$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.delete),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.delete(organisationId, action.id),
        ).pipe(
          map((payload) => TemplateActions.deleteSuccess({ id : payload.id })),
          catchError((error) =>
            of(
              TemplateActions.deleteFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public deleteFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.deleteFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public duplicate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.duplicate),
      withLatestFrom(
        this.store.select(selectActiveOrganisationId),
        this.store.select(selectSearchedParams),
      ),
      switchMap(([action, organisationId, searchParams]) =>
        from(
          this.service.duplicate(organisationId, action.id),
        ).pipe(
          retry(2),
          map((payload: Template) => {
            this.store.dispatch(TemplateActions.search({ params: searchParams }));

            return TemplateActions.duplicateSuccess({ payload });
          }),
          catchError((error) =>
            of(
              TemplateActions.duplicateFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public duplicateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.duplicateFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public duplicateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TemplateActions.duplicateSuccess),
        tap(({ payload }) => {
          // Brief delay to show success message, then navigate to edit page
          setTimeout(() => {
            // Use window.location.href for a clean navigation that always works
            const newUrl = `/jobs-and-tests/templates/${encodeURIComponent(payload.id)}`;
            window.location.href = newUrl;
          }, 1500);
        }),
      ),
    { dispatch: false },
  );

  public constructor(
    private readonly actions$: Actions,
    private readonly service: TemplateService,
    private readonly router: Router,
    private readonly store: Store,
  ) {}
}
